/* ═══════════════════════════════════════════════════════════════════════
   GRG shared form-field validator.
   Applies to any input carrying `data-grg-validate="<kind>"`.

   Supported kinds:
     email         → email address, RFC-compatible-ish regex
     phone         → international phone, digits 7–15 (allows +, space, -, (, ))
     name          → letters, spaces, hyphens, apostrophes, min 2 chars
     required-text → any non-empty string of min 2 chars

   Behaviour:
     - Validates on blur, and re-validates on input while the field is invalid
       so the customer sees the error clear the moment it becomes valid.
     - Renders an inline error message directly under the field (not the
       native browser tooltip, which hides on scroll).
     - Blocks form submission with the first invalid field focused.
     - Wires to Shopify's native `{% form 'contact' %}` — does NOT prevent
       Shopify's own server validation from also running.
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  // ── Rule set ────────────────────────────────────────────────────────
  var RULES = {
    email: {
      test: function (v) {
        v = (v || '').trim();
        if (!v) return { ok: false, msg: 'Email is required.' };
        // Reasonable email pattern: local@domain.tld
        var re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
        if (!re.test(v)) return { ok: false, msg: 'Please enter a valid email address.' };
        if (v.length > 254) return { ok: false, msg: 'Email is too long.' };
        return { ok: true };
      }
    },
    phone: {
      test: function (v) {
        v = (v || '').trim();
        if (!v) return { ok: false, msg: 'Phone number is required.' };
        if (!/^[0-9+\-\s().]+$/.test(v)) {
          return { ok: false, msg: 'Phone can only contain digits, +, -, spaces or ( ).' };
        }
        // Irish phone number rules (Republic of Ireland):
        //   +353 followed by 9 digits (drop leading 0 of local code), OR
        //   0 followed by 9 digits (local dialing)
        // Mobile prefixes: 083, 085, 086, 087, 089
        // Landline: 01 (Dublin), 021 (Cork), 022–025, 041–049, 051–059,
        //   061–069, 071–079, 090–099 etc.
        var digits = v.replace(/[^0-9+]/g, '');
        // Normalise +353 → 0 for length + prefix check
        var normalised = digits;
        if (normalised.startsWith('+353')) {
          normalised = '0' + normalised.slice(4);
        } else if (normalised.startsWith('00353')) {
          normalised = '0' + normalised.slice(5);
        } else if (normalised.startsWith('353') && normalised.length >= 12) {
          normalised = '0' + normalised.slice(3);
        }
        // Must now start with 0 and be exactly 10 digits total for standard
        // Irish numbers (some Freephone/Premium are 11 but we cover normal use).
        if (!/^0\d{8,9}$/.test(normalised)) {
          return { ok: false, msg: 'Please enter a valid Irish phone number (e.g. 086 123 4567 or +353 86 123 4567).' };
        }
        return { ok: true };
      }
    },
    name: {
      test: function (v) {
        v = (v || '').trim();
        if (!v) return { ok: false, msg: 'Name is required.' };
        if (v.length < 2) return { ok: false, msg: 'Name is too short.' };
        if (v.length > 80) return { ok: false, msg: 'Name is too long.' };
        // Letters (incl. accents), space, hyphen, apostrophe — no digits or symbols
        if (!/^[A-Za-zÀ-ÿ' \-.]+$/.test(v)) {
          return { ok: false, msg: 'Name can only contain letters, spaces, hyphens or apostrophes.' };
        }
        return { ok: true };
      }
    },
    'required-text': {
      test: function (v) {
        v = (v || '').trim();
        if (!v) return { ok: false, msg: 'This field is required.' };
        if (v.length < 2) return { ok: false, msg: 'Please enter more detail.' };
        return { ok: true };
      }
    }
  };

  // ── Error DOM helpers ───────────────────────────────────────────────
  function errorNodeFor(field) {
    var id = field.getAttribute('data-grg-err-for');
    if (id) {
      var existing = document.getElementById(id);
      if (existing) return existing;
    }
    // Create one on demand. Insert INSIDE the field's own column wrapper
    // (label, or the closest .fqf__field / .ctabs__field / .grg-enquiry__field
    // / .drf-form__field / .prt-tabs__field / .contact-hero__field / any parent
    // <label> or field-scoped element), NOT as a grid-sibling that could land
    // in a neighbour column. Falls back to inserting right after the field.
    var slot = document.createElement('div');
    slot.className = 'grg-field-error';
    slot.id = 'grg-err-' + Math.random().toString(36).slice(2, 8);
    field.setAttribute('data-grg-err-for', slot.id);
    var scope = field.closest('label')
      || field.closest('.fqf__field, .ctabs__field, .grg-enquiry__field, .drf-form__field, .prt-tabs__field, .contact-hero__field, .contact-form__field, .pts-form__field, .fqf__fieldset, [data-grg-field]');
    if (scope) {
      scope.appendChild(slot);
    } else if (field.parentNode) {
      // Last-resort: place immediately after the input itself.
      if (field.nextSibling) {
        field.parentNode.insertBefore(slot, field.nextSibling);
      } else {
        field.parentNode.appendChild(slot);
      }
    }
    return slot;
  }

  function showError(field, msg) {
    field.classList.add('grg-field-invalid');
    field.setAttribute('aria-invalid', 'true');
    var slot = errorNodeFor(field);
    if (slot) {
      slot.textContent = msg;
      slot.classList.add('grg-field-error--shown');
    }
  }

  function clearError(field) {
    field.classList.remove('grg-field-invalid');
    field.setAttribute('aria-invalid', 'false');
    var slot = errorNodeFor(field);
    if (slot) {
      slot.textContent = '';
      slot.classList.remove('grg-field-error--shown');
    }
  }

  // ── Validate a single field ─────────────────────────────────────────
  function validateField(field, showEmpty) {
    // Skip fields the user cannot see or that are optional and empty.
    if (field.disabled) return true;
    if (field.type === 'hidden') return true;
    if (field.offsetParent === null && !field.hasAttribute('data-grg-force')) return true;

    var kind = field.getAttribute('data-grg-validate');
    var rule = RULES[kind];
    if (!rule) return true;

    var v = field.value || '';
    var isRequired = field.hasAttribute('required') || field.hasAttribute('data-grg-required');

    // Empty + not required + not showing empty errors → treat as valid.
    if (!v.trim() && !isRequired && !showEmpty) {
      clearError(field);
      return true;
    }

    var res = rule.test(v);
    if (!res.ok) {
      showError(field, res.msg);
      return false;
    }
    clearError(field);
    return true;
  }

  // ── Wire a whole form ───────────────────────────────────────────────
  function wireForm(form) {
    if (form.hasAttribute('data-grg-validated')) return;
    form.setAttribute('data-grg-validated', 'true');

    var fields = form.querySelectorAll('[data-grg-validate]');
    if (!fields.length) return;

    fields.forEach(function (field) {
      var kind = field.getAttribute('data-grg-validate');

      // Hard-block invalid characters in the phone field as the user types,
      // so they can't paste or key in letters at all.
      if (kind === 'phone') {
        var filterPhone = function () {
          var cleaned = (field.value || '').replace(/[^0-9+\-\s().]/g, '');
          if (cleaned !== field.value) field.value = cleaned;
        };
        field.addEventListener('input', filterPhone);
        field.addEventListener('paste', function () {
          setTimeout(filterPhone, 0);
        });
      }

      // Live re-validate while user types, but only after the first invalid
      // check has fired (so we don't shout errors as they start typing).
      var wasInvalid = false;
      field.addEventListener('blur', function () {
        var ok = validateField(field, false);
        wasInvalid = !ok;
      });
      field.addEventListener('input', function () {
        if (wasInvalid) {
          var ok = validateField(field, false);
          if (ok) wasInvalid = false;
        }
      });
    });

    form.addEventListener('submit', function (e) {
      var firstInvalid = null;
      fields.forEach(function (field) {
        var ok = validateField(field, true);
        if (!ok && !firstInvalid) firstInvalid = field;
      });
      if (firstInvalid) {
        e.preventDefault();
        try {
          firstInvalid.focus({ preventScroll: false });
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (_) {
          firstInvalid.focus();
        }
      }
    });
  }

  function init() {
    // Sanity: remove any stray error slots from a prior render — they may
    // be sitting in the wrong grid cell after a hot reload.
    document.querySelectorAll('.grg-field-error').forEach(function (n) {
      if (n.parentNode) n.parentNode.removeChild(n);
    });
    document.querySelectorAll('[data-grg-err-for]').forEach(function (f) {
      f.removeAttribute('data-grg-err-for');
    });
    // Discover forms by finding any [data-grg-validate] field and walking up
    // to its owning form. That way we don't have to enumerate class names.
    var seen = new Set();
    document.querySelectorAll('[data-grg-validate]').forEach(function (field) {
      var form = field.closest('form');
      if (form && !seen.has(form)) {
        seen.add(form);
        form.removeAttribute('data-grg-validated');
        wireForm(form);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-scan after full page load in case a section rendered its form late.
  window.addEventListener('load', init);
  // Re-scan when Shopify's theme editor swaps a section in preview.
  document.addEventListener('shopify:section:load', init);
})();
